import { GoalsCollection } from '../../../coaching-commitment/goals-collection.model';
import { PayloadAction } from '../../../store.model';
import {
    formatFocusesForPerformer,
    formatFocusesForTeam,
    makeGrid as makeFocusHistoryGrid,
} from '../focus-history/focus-history.function';
import {
    formatUserBehaviours,
    getBehaviourById,
    getSubBehaviourById,
    makeGrid as makeFocusSelectionGrid,
    makeRows as makeFocusSelectionRows,
} from '../focus-selection/focus-selection.function';
import { ActionType } from '../focus-selector.action';
import { FocusSelectorState } from '../focus-selector.state';
import {
    focusForPerformerFocusHistoryToCommitmentKPI,
    setupLoadGoalsState,
    sortGoalThresholds,
} from '../load-goals/load-goals.function';
import {
    formatCoachableMetrics,
    makeGrid as makeMetricSelectionGrid,
} from '../metric-selection/metric-selection.function';
import { FocusHistoryGrid } from '../model/focus-history.grid.model';
import { FocusSelectorFocusForTeam } from '../model/focus-selector-focus-for-team.model';
import { FocusSelectorFocusModel } from '../model/focus-selector-focus.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';

import { FocusSelectorInit, FocusSelectorInitResult } from './init.action';

export function focusSelectorInitReducer(
    state: FocusSelectorState,
    action: PayloadAction<ActionType, FocusSelectorInit>,
): FocusSelectorState {
    const activeMetric = {
        id: action.payload?.activeMetricId ?? null,
    };

    const selectedFocus = {
        behaviour: {
            id: action.payload?.selectedFocus?.behaviourId ?? null,
            name: null,
        },

        subBehaviour: {
            id: action.payload?.selectedFocus?.subBehaviourId ?? null,
            name: null,
        },
    };

    return {
        ...state,
        pending: {
            initMain: true,
        },

        activeMetric,
        selectedFocus,

        performerId: action.payload.performerId,

        triggerSource: action.payload.triggerSource,

        goalsCollection: action.payload.goalsCollection,
    };
}

export function focusSelectorInitResultMainReducer(
    state: FocusSelectorState,
    action: PayloadAction<ActionType, FocusSelectorInitResult>,
): FocusSelectorState {
    const {
        active,
        closed,
        other,
        teamActive,
        teamClosed,
        initSelectedFocus,
        userBehaviours,
        coachableMetrics,
        goalThresholds,
        commitmentId,
    } = action.payload;

    const formattedMetrics = formatCoachableMetrics(coachableMetrics);

    const activeFocusesForPerformer = formatFocusesForPerformer(active);

    const otherFocusesForPerformer = formatFocusesForPerformer(other);

    const recentlyClosedFocusesForPerformer = formatFocusesForPerformer(
        closed,
        activeFocusesForPerformer,
    );

    const activeFocusesForTeam = formatFocusesForTeam(
        teamActive,
        activeFocusesForPerformer,
    );

    const recentlyClosedFocusesForTeam = formatFocusesForTeam(
        teamClosed,
        activeFocusesForPerformer,
    );

    const activeCommitmentGoalMetricIds = [
        ...activeFocusesForPerformer,
        ...otherFocusesForPerformer,
    ].reduce<number[]>((metricArray, focus) => {
        if (focus.metricId != null) {
            metricArray.push(focus.metricId);
        }
        return metricArray;
    }, []);

    const formattedBehaviours = formatUserBehaviours(
        userBehaviours,
        activeFocusesForPerformer,
    );

    let activeMetric: FocusSelectorMetricModel = { ...state.activeMetric };

    if (
        initSelectedFocus?.metrics?.length != null &&
        initSelectedFocus.metrics.length > 0
    ) {
        activeMetric.id = initSelectedFocus.metrics[0];
    }

    if (state.goalsCollection?.commitmentKpis?.[0]?.metricId != null) {
        activeMetric.id = state.goalsCollection.commitmentKpis[0].metricId;
    }

    if (activeMetric.id != null) {
        const foundActiveMetric = formattedMetrics.find(
            (metric) => metric.id === activeMetric.id,
        );

        if (foundActiveMetric != null) {
            activeMetric = foundActiveMetric;
        }
    }

    const selectedFocus: FocusSelectorFocusModel = { ...state.selectedFocus };

    if (selectedFocus?.behaviour?.id != null) {
        const foundBehaviour = getBehaviourById(
            selectedFocus?.behaviour?.id,
            formattedBehaviours,
        );
        selectedFocus.behaviour.name = foundBehaviour?.name ?? '';

        if (selectedFocus?.subBehaviour?.id != null) {
            const foundSubBehaviour = getSubBehaviourById(
                selectedFocus?.subBehaviour?.id,
                selectedFocus?.behaviour?.id,
                formattedBehaviours,
            );

            selectedFocus.subBehaviour.name = foundSubBehaviour?.name ?? '';
        }
    }

    let focusSelectionGrid = makeFocusSelectionGrid(
        formattedBehaviours,
        activeMetric?.id,
    );

    if (
        activeMetric?.id != null &&
        selectedFocus?.behaviour?.id != null &&
        selectedFocus?.subBehaviour?.id != null
    ) {
        focusSelectionGrid = {
            ...focusSelectionGrid,
            rows: makeFocusSelectionRows(
                formattedBehaviours,
                activeMetric.id,
                selectedFocus.subBehaviour.id,
            ),
            filterByBehaviourId: null,
            selection: {
                behaviourId: selectedFocus?.behaviour?.id ?? null,
                subBehaviourId: selectedFocus?.subBehaviour?.id ?? null,
            },
        };
    }

    const sortedGoalThresholds = sortGoalThresholds(goalThresholds);

    const combinedGoalsCollection: GoalsCollection = {
        ...state.goalsCollection,
        goalsFromOtherCommitments: [
            ...active.map((activeGoal) =>
                focusForPerformerFocusHistoryToCommitmentKPI(activeGoal),
            ),
            ...other.map((otherGoal) =>
                focusForPerformerFocusHistoryToCommitmentKPI(otherGoal),
            ),
        ],
    };

    const { goals, selectedGoals, loadGoalsGrid, updatedGoalsCollection } =
        setupLoadGoalsState(
            activeMetric,
            selectedFocus,
            formattedMetrics,
            formattedBehaviours,
            initSelectedFocus?.metrics,
            combinedGoalsCollection,
            state.loadGoalsGrid,
            commitmentId,
        );

    return {
        ...state,
        pending: {
            ...state.pending,
            initMain: false,
        },

        activeMetric,
        selectedFocus,

        goals,
        selectedGoals,
        goalsCollection: updatedGoalsCollection,

        activeCommitmentGoalMetricIds,

        goalThresholds: sortedGoalThresholds,

        initSelectedMetricIds: initSelectedFocus?.metrics ?? [],
        coachableMetrics: formattedMetrics,
        behaviours: formattedBehaviours,

        activeFocusesForPerformer,
        recentlyClosedFocusesForPerformer,
        otherFocusesForPerformer,
        activeFocusesForTeam,
        recentlyClosedFocusesForTeam,

        focusSelectionGrid,
        loadGoalsGrid,

        metricSelectionGrid: makeMetricSelectionGrid(
            formattedMetrics,
            activeMetric,
        ),
        activeFocusesPerformerGrid: makeFocusHistoryGrid(
            'performer',
            activeFocusesForPerformer,
        ),
        recentlyClosedFocusesPerformerGrid: makeFocusHistoryGrid(
            'performer',
            recentlyClosedFocusesForPerformer,
        ),
        activeFocusesTeamGrid: makeFocusHistoryGrid(
            'team',
            activeFocusesForTeam,
        ) as FocusHistoryGrid<FocusSelectorFocusForTeam>,
        recentlyClosedFocusesTeamGrid: makeFocusHistoryGrid(
            'team',
            recentlyClosedFocusesForTeam,
        ) as FocusHistoryGrid<FocusSelectorFocusForTeam>,
    };
}
